import { Button, Icon, Image, Modal, Typography } from '@smartgift/frontend-toolkit';
import React, { useState, useEffect, useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { SkeletonLoaders } from '@hero/components';
import { Card } from '@hero/components/atoms';
import { ModalValidationErrors } from '@hero/components/molecules';
import { DonationOptions } from '@hero/components/organisms';
import { useValidationErrorsStore } from '@hero/components/providers/ValidationErrorsProvider';
import { constants } from '@hero/configs';
import { CHARITY_LIST_PAGINATION_LIMIT, PRODUCT_TYPES, VALIDATION_ERRORS } from '@hero/constants';
import { useSearchProducts } from '@hero/services/product-service/hooks';
import { useNotification } from '@hero/store/hooks';
import { getSafeInnerHtml } from '@hero/utils';

import * as S from './style';

function AvailableCharitiesModal({ isOpen, setIsOpen, selectedCharities, setSelectedCharities }) {
  const { t } = useTranslation();
  const [selectedCharity, setSelectedCharity] = useState(selectedCharities || []);
  const { showNotification, close, reset: resetNotification } = useNotification();
  const { addModalError, reset } = useValidationErrorsStore();

  const pageLayoutContentRef = useRef(null);

  const {
    data: charities,
    isFetching,
    fetchNextPage
  } = useSearchProducts({
    params: {
      filters: {
        charityOnly: true
      },
      options: {
        pagination: false
      }
    },
    options: {
      enabled: isOpen,
      staleTime: Infinity,
      cacheTime: Infinity
    }
  });

  const checkCustomErrors = async () => {
    let hasCustomError = false;
    if (selectedCharity?.length === 0) {
      await addModalError(<Trans i18nKey={VALIDATION_ERRORS.noSelectedCharity} />);
      hasCustomError = true;
    }
    if (selectedCharity.find(item => item.skus?.length === 0)) {
      await addModalError(<Trans i18nKey={VALIDATION_ERRORS.noSelectedCharitySku} />);
      hasCustomError = true;
    }
    return hasCustomError;
  };

  const primaryButtonClickHandler = async () => {
    const hasCustomError = await checkCustomErrors();
    if (!hasCustomError) {
      setSelectedCharities(selectedCharity);
      setIsOpen(false);
    }
  };

  const onCharitySelect = (charity, skus) => {
    if (skus?.length) {
      const newList = selectedCharity.filter(
        item => item.merchantProductId !== charity.merchantProductId
      );

      setSelectedCharity([
        ...newList,
        {
          ...charity,
          skus: skus,
          meta: {
            ...charity.meta,
            productType: PRODUCT_TYPES.CHARITY
          }
        }
      ]);
    } else if (selectedCharity.find(item => item.merchantProductId === charity.merchantProductId)) {
      const newList = selectedCharity
        .filter(item => item.merchantProductId !== charity.merchantProductId)
        .map(item => ({
          ...item,
          meta: {
            ...item.meta,
            productType: PRODUCT_TYPES.CHARITY
          }
        }));
      setSelectedCharity(newList);
    } else {
      setSelectedCharity([
        ...selectedCharity,
        {
          ...charity,
          skus: [],
          meta: {
            ...charity.meta,
            productType: PRODUCT_TYPES.CHARITY
          }
        }
      ]);
    }
  };

  const onMoreInformationClick = charity => {
    setIsOpen(false);
    showNotification({
      notificationImage: <Image src={charity?.images?.[0]?.url} width={180} />,
      header: charity.name,
      bodyStyle: { overflowY: 'hidden' },
      customContent: () => (
        <S.MoreInformationModalBody>
          <Typography variant="body2">
            <div {...getSafeInnerHtml(charity.desc)} />
          </Typography>
          <Typography variant="caption1">
            <div {...getSafeInnerHtml(charity.shortDesc)} />
          </Typography>
        </S.MoreInformationModalBody>
      ),
      onBack: () => {
        close();
        resetNotification();
        setIsOpen(true);
      },
      primaryButtonProps: {
        children: t('okay'),
        onClick: () => {
          close();
          resetNotification();
        }
      },
      secondaryButtonProps: {
        children: t('back'),
        onClick: () => {
          close();
          resetNotification();
          setIsOpen(true);
        }
      }
    });
  };

  useEffect(() => {
    return () => {
      reset();
    };
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={setIsOpen}
      size="lg"
      primaryButtonProps={{
        children: t('confirmSelections'),
        onClick: primaryButtonClickHandler,
        disabled: isFetching
      }}
      secondaryButtonProps={{ children: t('cancel') }}
      title={t('availableCharities')}
    >
      <ModalValidationErrors pageLayoutContentRef={pageLayoutContentRef} />
      <S.ModalBody>
        <Typography variant="body1Bold">{t('availableCharitiesModalDesc')}</Typography>
        <Typography variant="body2" emphasis="weak" style={{ marginBottom: 16 }}>
          <Trans
            i18nKey="availableCharitiesModalSubDesc"
            components={{
              b: <b />,
              a: <a href="mailto:support@smartgiftit.com" target="_blank" rel="noreferrer" />,
              u: <u />
            }}
          />
        </Typography>

        {isFetching ? (
          <SkeletonLoaders.CharityLoader count={CHARITY_LIST_PAGINATION_LIMIT} />
        ) : (
          <>
            <S.ListHeader>
              <Typography variant="body2">
                <Trans
                  i18nKey="amountCharities"
                  values={{ count: charities?.docs?.length }}
                  components={{ b: <b /> }}
                />
              </Typography>
              {/* <S.ListHeaderRightContent ref={rightContentRef}>
                <SortBy
                  value={sort}
                  onChange={data => addQuery('sort', data.value)}
                  rightContentRef={rightContentRef}
                />
              </S.ListHeaderRightContent> */}
            </S.ListHeader>
            <S.ContentList length={charities?.docs?.length}>
              {charities?.docs?.map(charity => (
                <S.ContentItem
                  key={charity.merchantProductId}
                  active={
                    !!selectedCharity?.find(
                      item => item.merchantProductId === charity.merchantProductId
                    )
                  }
                >
                  <S.ContentItemImage
                    src={charity?.images[0]?.url}
                    alt={charity.name}
                    width={180}
                    height={198}
                  />
                  <S.ContentItemBody>
                    <S.ContentContextWrapper>
                      <S.ContentItemHeader length={charities?.docs?.length}>
                        <Card>
                          <Image
                            src={
                              charity.meta?.logoUrl || constants.brands.data[charity.brand.id].logo
                            }
                            alt={charity.name}
                            width="auto"
                            height={28}
                          />
                        </Card>
                        <S.ContentItemSelectBadge
                          role="button"
                          tabIndex={0}
                          onClick={() => onCharitySelect(charity)}
                        >
                          {!!selectedCharity?.find(
                            item => item.merchantProductId === charity.merchantProductId
                          ) && <Icon name="success" />}
                        </S.ContentItemSelectBadge>
                      </S.ContentItemHeader>
                      <Typography
                        variant="body2Bold"
                        style={{
                          display: '-webkit-box',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: 'vertical',
                          height: 21,
                          width: charities?.docs?.length === 1 ? '100%' : 240
                        }}
                      >
                        {charity.name}
                      </Typography>
                      <Typography variant="body2">
                        <div
                          {...getSafeInnerHtml(charity.desc)}
                          style={{
                            display: '-webkit-box',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            height: 42,
                            width: charities?.docs?.length === 1 ? '100%' : 240
                          }}
                        />
                      </Typography>
                      <Button
                        variant="link"
                        color="neutral"
                        size="sm"
                        style={{ marginTop: 4 }}
                        onClick={() => onMoreInformationClick(charity)}
                      >
                        {t('moreInformation')}
                      </Button>
                    </S.ContentContextWrapper>
                    <DonationOptions
                      charity={charity}
                      selectedSkus={
                        selectedCharity.find(
                          item => item.merchantProductId === charity.merchantProductId
                        )?.skus
                      }
                      setSelectedSkus={skus => {
                        onCharitySelect(charity, skus);
                      }}
                      style={{ flexWrap: 'wrap' }}
                    />
                  </S.ContentItemBody>
                </S.ContentItem>
              ))}
            </S.ContentList>
          </>
        )}

        {charities?.pagination?.hasNextPage && (
          <Button
            variant="text"
            disabled={isFetching}
            uppercase={false}
            onClick={() => {
              fetchNextPage();
            }}
            style={{ margin: '0 auto' }}
          >
            {isFetching ? t('loading') : t('loadMore')}
          </Button>
        )}
        <Typography
          variant="caption1"
          emphasis="weak"
          style={{ marginTop: 8, textAlign: 'center' }}
        >
          {t('availableCharitiesModalFooterText')}
        </Typography>
      </S.ModalBody>
    </Modal>
  );
}

export { AvailableCharitiesModal };
